import { Box, Divider, Drawer, List, useMediaQuery, useTheme } from '@mui/material';
import NavigationBarItem from './NavigationBarItem';
import NavigationBarFooter from './NavigationBarFooter';
import { PropsWithChildren, useEffect, useState } from 'react';
import useUserTerminals from '@hooks/useUserTerminals';
import { AppLocalStorageKeys } from '../../Configuration/types';
import { MobileMenuProps, NavItemProps } from './types';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import LoadTenderIcon from './NavbarItems/LoadTenderItem';
import TravelExploreIconTwoTone from '@mui/icons-material/TravelExploreTwoTone';
import WebAssetTwoTone from '@mui/icons-material/WebAssetTwoTone';
import EditLocationTwoToneIcon from '@mui/icons-material/EditLocationTwoTone';
import AssessmentTwoToneIcon from '@mui/icons-material/AssessmentTwoTone';
import PaidTwoToneIcon from '@mui/icons-material/PaidTwoTone';
import { queueModal } from '@components/Modals/modalSlice';
import DocumentControlItem from './NavbarItems/DocumentControlItem';
import ReleaseNotes from './NavbarItems/ReleaseNotes';
import { selectAppFeatures } from '@/Infrastructure/Utilities/appSelectors';
import { ContactSupportListItem } from './NavbarItems/ContactSupport';
import RemoteAssistanceListItem from './NavbarItems/RemoteAssistance';
import MotorCityRelayListItem from './NavbarItems/MotorCityRelay';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';

import styles from './NavigationBar.module.css';

/**
 * Navigation (Side) Bar for the user to navigate through the site pages. Displays current application version in footer.
 */
export default function NavigationBar(props: MobileMenuProps) {
  // Retrieve the default value from local storage
  const [isDesktopOpen, setIsDesktopOpen] = useState(localStorage.getItem(AppLocalStorageKeys.NavbarOpen) === 'false' ? false : true);
  const appDispatch = useAppDispatch();
  const featureFlags = useAppSelector((state) => selectAppFeatures(state));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { terminals } = useUserTerminals();
  const { data: user } = useFetchMyProfileQuery(undefined);
  const selectedTerminal = useAppSelector((state) => state.applicationState.selectedTerminal);

  useEffect(() => {
    localStorage.setItem(AppLocalStorageKeys.NavbarOpen, isDesktopOpen ? 'true' : 'false');
  }, [isDesktopOpen]);

  // If the user has no terminals we cannot render the links
  if (terminals.length === 0) {
    return <></>;
  }

  return (
    <NavigationBarContainer
      isMobile={isMobile}
      isDesktopOpen={isDesktopOpen}
      {...props}>
      <List className={styles['scroll-list']}>
        <NavigationBarItem
          key='navbar-item-board'
          item={{
            displayText: 'Board',
            destination: `${selectedTerminal?.number ?? ''}/board`,
            icon: <WebAssetTwoTone />,
            title: 'Dispatch Board'
          }}
          open={isDesktopOpen}
          isMobile={isMobile}
          {...props}
        />
        <NavigationBarItem
          key='navbar-item-research'
          item={{
            displayText: 'Research',
            destination: `${selectedTerminal?.number ?? ''}/research`,
            icon: <TravelExploreIconTwoTone />,
            title: 'Research'
          }}
          open={isDesktopOpen}
          isMobile={isMobile}
          {...props}
        />
        <NavigationBarItem
          key='navbar-item-consignee-maintenance'
          item={{
            displayText: 'Consignee Maintenance',
            icon: <EditLocationTwoToneIcon />,
            title: 'Consignee Maintenance',
            onClick: () => {
              appDispatch(
                queueModal({
                  Component: 'consigneeModal',
                  Props: {
                    enableSelection: true,
                    title: 'Consignee Maintenance'
                  }
                })
              );
            }
          }}
          open={isDesktopOpen}
          isMobile={isMobile}
          {...props}
        />
        <NavigationBarItem
          key='navbar-item-consignee-reports'
          item={{
            displayText: 'Reports',
            icon: <AssessmentTwoToneIcon />,
            title: 'Reports',
            onClick: () => {
              appDispatch(
                queueModal({
                  Component: 'reportModal',
                  Props: {
                    enableSelection: true,
                    title: 'Reports'
                  }
                })
              );
            }
          }}
          open={isDesktopOpen}
          isMobile={isMobile}
          {...props}
        />
        <DocumentControlItem
          isDesktopOpen={isDesktopOpen}
          isMobile={isMobile}
          {...props}
        />
        <LoadTenderIcon
          isDesktopOpen={isDesktopOpen}
          isMobile={isMobile}
          {...props}
        />
        {featureFlags?.agentSettlements && user?.canViewAgtSettlements && !selectedTerminal?.isCompanyStore && (
          <NavigationBarItem
            key='navbar-item-agent-settlements'
            item={{
              displayText: 'Agent Settlements',
              destination: `${selectedTerminal?.number ?? ''}/settlements`,
              icon: <PaidTwoToneIcon />,
              title: 'Agent Settlements'
            }}
            open={isDesktopOpen}
            isMobile={isMobile}
            {...props}
          />
        )}
        <MotorCityRelayListItem
          isDesktopOpen={isDesktopOpen}
          isMobile={isMobile}
          {...props}
        />
      </List>

      <Divider sx={{ mx: '4px', borderColor: 'var(--mui-palette-primary-contrastText)', opacity: '0.6' }} />

      <RemoteAssistanceListItem
        isDesktopOpen={isDesktopOpen}
        isMobile={isMobile}
        {...props}
      />

      <ContactSupportListItem
        isDesktopOpen={isDesktopOpen}
        isMobile={isMobile}
        {...props}
      />

      <ReleaseNotes
        isDesktopOpen={isDesktopOpen}
        isMobile={isMobile}
        {...props}
      />

      <NavigationBarFooter
        isMobile={isMobile}
        isDesktopOpen={isDesktopOpen}
        setDesktopOpen={setIsDesktopOpen}
        {...props}
      />
    </NavigationBarContainer>
  );
}

/**
 * Local function used for creating the container for the navigation bar as it has different requirement
 * between desktop and mobile screen resolutions.
 */
function NavigationBarContainer(props: PropsWithChildren & NavItemProps) {
  if (props.isMobile) {
    // Try to find the appbar and align with the bottom of it. Default to app bar height.
    // There seems to be a render race where this component is happening before the Appbar exists.
    const appBar = document.getElementsByTagName('header');
    const marginTop = `${String(appBar.length > 0 ? appBar[0].getBoundingClientRect().bottom : 48)}px`;

    return (
      <Drawer
        anchor='top'
        open={props.mobileMenuOpen}
        onClose={() => {
          props.setMobileMenuOpen(!props.mobileMenuOpen);
        }}
        slotProps={{
          paper: {
            className: styles['drawer-paper'],
            sx: {
              marginTop: marginTop
            }
          }
        }}
        transitionDuration={300}
        disablePortal>
        <Box
          role='presentation'
          className={`${styles.brand} ${styles['mobile-container']}`}>
          {props.children}
        </Box>
      </Drawer>
    );
  }

  return (
    <div className={`${styles.brand} ${styles['navigation-bar']} ${props.isDesktopOpen ? styles.open : styles.closed}`}>
      {props.children}
    </div>
  );
}
